'use client';

import { ROUTES } from '@/constants/routes';
import { signIn, getProviders } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { signUpSchema } from '@zaikoo/core/domains/auth/schemas/AuthSchemas';
import { FormShell, Glyph, OAuthGlyph, strength, useLastUsed } from './parts';

interface KernoSignUpProps {
  /**
   * Dev design-faithful mode (true on this dev server): render the full designed
   * OAuth set (Google + Apple) even for unconfigured providers, matching the
   * design. Visual-only for unconfigured providers — clicking shows a notice,
   * never a broken redirect. False in real production → configured providers only.
   */
  showDesignedOAuth?: boolean;
}

/**
 * Derive an organization name for signUp (the schema requires one) without a
 * visible field — the design collects no company name; the onboarding
 * CompanyName step renames it immediately after. Must satisfy
 * `organizationNameSchema`: 2–100 chars of [A-Za-z0-9 &.-] (NO apostrophes).
 * Prefer the email-domain label, else "<First> Workspace".
 */
function deriveOrgName(email: string, fullName: string): string {
  const domain = (email.split('@')[1]?.split('.')[0] ?? '').replace(/[^A-Za-z0-9 &.-]/g, '');
  if (domain.length >= 2) {
    return (domain.charAt(0).toUpperCase() + domain.slice(1)).slice(0, 100);
  }
  const first = (fullName.trim().split(/\s+/)[0] ?? '').replace(/[^A-Za-z0-9 &.-]/g, '');
  return (first ? `${first} Workspace` : 'New Workspace').slice(0, 100);
}

export function KernoSignUp({ showDesignedOAuth = false }: KernoSignUpProps) {
  const router = useRouter();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [formReady, setFormReady] = useState(false);
  const [providers, setProviders] = useState<Record<string, { id: string; name: string }> | null>(
    null
  );
  const [lastUsed, markUsed] = useLastUsed();

  useEffect(() => {
    setFormReady(true);
    getProviders().then(setProviders);
  }, []);

  const st = strength(pw);
  const ok = Boolean(name && email && pw.length >= 8 && agree);

  // Render a provider's button when it's configured, or — in dev design-faithful
  // mode — always, so the designed Google + Apple row matches the design.
  const showGoogle = Boolean(providers?.google) || showDesignedOAuth;
  const showApple = Boolean(providers?.apple) || showDesignedOAuth;
  const hasOAuth = showGoogle || showApple;

  // Preserve the ?preview=1 hatch across the cross-link in dev.
  const signinHref = showDesignedOAuth ? `${ROUTES.AUTH.SIGNIN}?preview=1` : ROUTES.AUTH.SIGNIN;

  const handleOAuth = async (provider: 'google' | 'apple') => {
    setError('');
    // Providers list still loading — ignore the click rather than show a false
    // "not configured" notice for a provider that may in fact be configured.
    if (!providers) return;
    // Design-faithful mode may render a button for an unconfigured provider —
    // never fire a redirect that would 404 / error.
    if (!providers[provider]) {
      setError(
        `${provider === 'apple' ? 'Apple' : 'Google'} sign-up isn't configured in this environment yet.`
      );
      return;
    }
    markUsed(provider);
    setBusy(true);
    try {
      // New OAuth users are provisioned in the NextAuth signIn callback, then
      // land in onboarding to name their workspace.
      await signIn(provider, { callbackUrl: ROUTES.ONBOARDING.ROOT });
    } catch {
      setError('An error occurred. Please try again.');
      setBusy(false);
    }
  };

  const signUpMutation = api.auth.signUp.useMutation({
    onSuccess: async () => {
      const result = await signIn('credentials', { email, password: pw, redirect: false });
      // Email verification is async; auto-sign-in proceeds to onboarding either way.
      const destination = result?.ok ? ROUTES.ONBOARDING.ROOT : ROUTES.AUTH.SIGNIN;
      window.location.assign(destination);
    },
    onError: err => {
      setError(err.message || 'Failed to create account. Please try again.');
      setBusy(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ok) return;
    setError('');
    const input = {
      email,
      password: pw,
      fullName: name,
      organizationName: deriveOrgName(email, name),
    };
    // Client-side validation parity with the server (uppercase/lowercase/number,
    // name characters, etc.) so valid-looking submissions don't fail after submit.
    const parsed = signUpSchema.safeParse(input);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Please check your details and try again.');
      return;
    }
    setBusy(true);
    signUpMutation.mutate(parsed.data);
  };

  return (
    <FormShell
      headRight={
        <>
          Already on Kerno?
          <a
            href={signinHref}
            onClick={e => {
              e.preventDefault();
              router.push(signinHref);
            }}
          >
            Sign in
          </a>
        </>
      }
    >
      <div className="auth-eyebrow">
        <span className="dot" />
        Create account
      </div>
      <h1 className="auth-title">Run leaner inventory.</h1>
      <p className="auth-sub">Free 14-day trial. No card required.</p>

      {error && (
        <div className="auth-banner error">
          <span aria-hidden="true">
            <Glyph name="alert" size={16} />
          </span>
          <div>{error}</div>
        </div>
      )}

      {hasOAuth && (
        <>
          <div className="auth-oauth auth-oauth--two">
            {showGoogle && (
              <button
                type="button"
                className={`oauth-btn ${lastUsed === 'google' ? 'is-last-used' : ''}`}
                onClick={() => handleOAuth('google')}
                disabled={busy}
              >
                <span className="oauth-glyph">
                  <OAuthGlyph provider="google" />
                </span>
                <span className="oauth-label">Sign up with Google</span>
                {lastUsed === 'google' && <span className="last-used-pill">Last used</span>}
              </button>
            )}
            {showApple && (
              <button
                type="button"
                className={`oauth-btn ${lastUsed === 'apple' ? 'is-last-used' : ''}`}
                onClick={() => handleOAuth('apple')}
                disabled={busy}
              >
                <span className="oauth-glyph">
                  <OAuthGlyph provider="apple" />
                </span>
                <span className="oauth-label">Sign up with Apple</span>
                {lastUsed === 'apple' && <span className="last-used-pill">Last used</span>}
              </button>
            )}
          </div>
          {/* OAuth sign-up carries terms acceptance via clickwrap (the email path
              uses the explicit checkbox below). */}
          <p className="auth-consent auth-consent--oauth">
            By continuing you agree to our <a href="/terms">Terms of Service</a> and{' '}
            <a href="/privacy">Privacy Policy</a>.
          </p>
          <div className="auth-divider">or with email</div>
        </>
      )}

      <form
        data-testid="auth-signup-form"
        data-ready={formReady ? 'true' : 'false'}
        onSubmit={handleSubmit}
      >
        <div className="auth-field">
          <div className="auth-field-label">Full name</div>
          <input
            className="auth-input"
            type="text"
            placeholder="Alex Morgan"
            autoComplete="name"
            aria-label="Full name"
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="auth-field">
          <div className="auth-field-label">Work email</div>
          <input
            className="auth-input"
            type="email"
            placeholder="you@company.com"
            autoComplete="email"
            aria-label="Work email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="auth-field">
          <div className="auth-field-label">Password</div>
          <div className="auth-input-group">
            <input
              className="auth-input"
              type={showPw ? 'text' : 'password'}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              aria-label="Password"
              value={pw}
              onChange={e => setPw(e.target.value)}
              disabled={busy}
            />
            <button
              type="button"
              className="auth-input-affix"
              onClick={() => setShowPw(s => !s)}
              aria-label={showPw ? 'Hide password' : 'Show password'}
            >
              <Glyph name={showPw ? 'eye-off' : 'eye'} size={16} />
            </button>
          </div>
          {pw && (
            <div className={`auth-strength ${st.tier}`}>
              <div className="auth-strength-bar">
                <span />
              </div>
              <div className="auth-strength-label">{st.label}</div>
            </div>
          )}
          <div className="auth-help">
            Use 12+ characters with a mix of letters, numbers and symbols.
          </div>
        </div>

        <div className="auth-checkbox-row">
          <input
            type="checkbox"
            checked={agree}
            onChange={e => setAgree(e.target.checked)}
            aria-label="I agree to the Terms of Service and Privacy Policy"
          />
          <span>
            I agree to the <a href="/terms">Terms of Service</a> and{' '}
            <a href="/privacy">Privacy Policy</a>.
          </span>
        </div>

        <div className="auth-submit-row">
          <button type="submit" className="auth-btn primary" disabled={!ok || busy}>
            {busy ? (
              <>
                <span className="spin" />
                Creating workspace…
              </>
            ) : (
              <>
                Create workspace <Glyph name="arrow-right" size={15} />
              </>
            )}
          </button>
        </div>
      </form>
    </FormShell>
  );
}
